import type { SyntheticEvent } from 'react';
import { memo } from 'react';
import type { CellContext } from '@tanstack/react-table';
import { formatDateWithMask, isDateValid } from 'utils/dateAndTime';
import type { TypographyProps } from '@mui/material';
import { styled, Typography } from '@mui/material';
import type { FormItemUiDefinition } from '../../../../types/formItem.ts';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../../utils/styles.ts';
import { getIcon } from '../../../../utils/common.ts';
import { ItemContainer } from '../../ItemContainer.tsx';

type CustomTypographyProps = TypographyProps & {
  isClickable?: boolean;
  isDefaultDate?: boolean;
  className?: string;
};

const StyledTypography = styled(({ isClickable, isDefaultDate, ...props }: CustomTypographyProps) => <Typography {...props} />, {
  shouldForwardProp: (prop) => prop !== 'isClickable' && prop !== 'isDefaultDate',
})(({ isClickable, isDefaultDate, theme }) => ({
  fontStyle: isDefaultDate ? 'italic' : 'normal',
  color: isDefaultDate ? theme?.palette.grey[500] : theme?.palette.text.primary,
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': {
    textDecoration: isClickable ? 'underline' : 'none',
  },
}));

interface Props<T, P> {
  value: string | string[] | null;
  row: CellContext<T, P>;
  onClick?: (row: T, cell: keyof P) => unknown;
  isClickable?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
}

const DateBase = <T, P>({ value, row, onClick, isClickable, formItemUiDefinition }: Props<T, P>) => {
  const containerStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
  const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);
  const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition.icon) : null;

  const dateValue = Array.isArray(value) ? value[0] : (value as string | null);
  const isValid = dateValue !== null && isDateValid(dateValue);

  const dateMask = Array.isArray(value) ? value[1] : '';
  const formattedDate = isValid ? formatDateWithMask(dateValue, dateMask) : '';

  const isDefaultDate = !isValid;

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    onClick?.(row.row.original, row.column.id as keyof P);
  };

  return (
    <ItemContainer styles={containerStyles}>
      {Icon && <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />}
      <StyledTypography
        variant="subtitle2"
        onClick={handleClick}
        isClickable={isClickable && !isDefaultDate}
        isDefaultDate={isDefaultDate}
        sx={textStyles}>
        {isDefaultDate ? '' : formattedDate}
      </StyledTypography>
    </ItemContainer>
  );
};

export const DateComponent = memo(DateBase) as typeof DateBase;
